import React, { memo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useNotes } from 'core/hooks';
import { Colors } from 'services/ColorService';

interface INotesPanelProps {
  studentId?: string;
  groupId?: string;
}

const SENTIMENTS = ['neutral', 'positive', 'negative'] as const;

/**
 * Free-form notes panel for a student or a group (spec 0003 Phase 2). Add /
 * pin / delete, with a plus/minus sentiment tag. Shared by StudentDetail and
 * GroupDetail.
 */
export const NotesPanel = memo(({ studentId, groupId }: INotesPanelProps) => {
  const { t } = useTranslation();
  const { notes, loading, saving, add, edit, remove } = useNotes({
    studentId,
    groupId,
  });

  const [body, setBody] = useState('');
  const [sentiment, setSentiment] = useState<string>('neutral');

  const onAdd = useCallback(async () => {
    if (!body.trim()) return;
    const ok = await add({ body: body.trim(), sentiment });
    if (ok) {
      setBody('');
      setSentiment('neutral');
    }
  }, [add, body, sentiment]);

  return (
    <Card>
      <CardTitle>{t('pages.notes.title')}</CardTitle>

      <Form>
        <TextArea
          value={body}
          placeholder={t('pages.notes.placeholder')}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
        />
        <Row>
          <Select
            value={sentiment}
            onChange={(e) => setSentiment(e.target.value)}
          >
            {SENTIMENTS.map((s) => (
              <option key={s} value={s}>
                {t(`pages.notes.sentiment.${s}`)}
              </option>
            ))}
          </Select>
          <AddButton
            type="button"
            disabled={saving || !body.trim()}
            onClick={onAdd}
          >
            {t('pages.notes.add')}
          </AddButton>
        </Row>
      </Form>

      {loading && notes.length === 0 ? (
        <Muted>{t('pages.notes.loading')}</Muted>
      ) : notes.length === 0 ? (
        <Muted>{t('pages.notes.empty')}</Muted>
      ) : (
        <List>
          {notes.map((note) => (
            <Item key={note.id} sentiment={note.sentiment}>
              <ItemBody>{note.body}</ItemBody>
              <ItemMeta>
                <span>{note.authorName || '—'}</span>
                <span>{(note.createdAt || '').slice(0, 10)}</span>
                <LinkButton
                  type="button"
                  onClick={() => edit({ id: note.id, pinned: !note.pinned })}
                >
                  {note.pinned ? t('pages.notes.unpin') : t('pages.notes.pin')}
                </LinkButton>
                <LinkButton
                  type="button"
                  tone="danger"
                  onClick={() => remove(note.id)}
                >
                  {t('pages.notes.delete')}
                </LinkButton>
              </ItemMeta>
            </Item>
          ))}
        </List>
      )}
    </Card>
  );
});

const Card = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 10px;
  background: ${Colors.White};
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${Colors.Gray};
  margin-bottom: 10px;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
  resize: vertical;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Select = styled.select`
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 6px 8px;
  font: inherit;
  font-size: 13px;
`;

const AddButton = styled.button`
  border: 1px solid ${Colors.Blue};
  background: ${Colors.Blue};
  color: ${Colors.White};
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${Colors.Gray};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const sentimentColor = (s: string): string => {
  if (s === 'positive') return Colors.SuccessDefault;
  if (s === 'negative') return Colors.ErrorDefault;
  return Colors.SecondLightGray;
};

const Item = styled.div<{ sentiment: string }>`
  border: 1px solid ${Colors.SecondLightGray};
  border-left: 3px solid ${({ sentiment }) => sentimentColor(sentiment)};
  border-radius: 8px;
  padding: 8px 10px;
`;

const ItemBody = styled.div`
  font-size: 13px;
  color: ${Colors.Black};
  white-space: pre-wrap;
`;

const ItemMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-top: 6px;
  font-size: 12px;
  color: ${Colors.Gray};
`;

const LinkButton = styled.button<{ tone?: 'danger' }>`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  color: ${({ tone }) =>
    tone === 'danger' ? Colors.ErrorDefault : Colors.Blue};
`;
